import React, { useContext, useEffect, useState } from 'react';
import {
    DefaultEffects,
    Nav,
    INavLink,
    INavStyles,
    INavLinkGroup,
    IconButton,
    IIconStyles,
} from '@fluentui/react';
import { routeKeys, getNavRoute } from '../../../Common/util/RouteUtils';
import { useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../../models/AuthContext';
import { CapabilityContext } from '../../../Common/components/Capabilities/CapabilityContext';
import { Capability } from '../../../Common/components/Capabilities/capability';
import { ThemeContext } from '../../models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../../Common/api/ABHub/ABHubGlobalConfig';
import { countPendingKustoAccessRequests } from '../../../ABSM/api/KustoAccessApi';
import { isKustoAccessConfiguredForCloud } from '../../../ABSM/components/KustoAccess/kustoAccessConfig';

export const PageNav: React.FC = () => {
    const location = useLocation();
    const { authContext } = useContext(AuthContext);
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const [isHidden, setIsHidden] = useState<boolean>(false);
    const [pendingKustoRequests, setPendingKustoRequests] = useState<number>(0);
    const [selectedRouteKey, setSelectedRouteKey] = useState<
        string | undefined
    >(getNavRoute(location.pathname));

    useEffect(() => {
        setSelectedRouteKey(getNavRoute(location.pathname));
    }, [location]);

    const collapseChevronStyles: IIconStyles = {
        root: {
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
        },
    };

    const navStyles: Partial<INavStyles> = {
        chevronIcon: {
            display: 'none',
        },
        chevronButton: {
            display: 'none',
        },
        linkText: {
            display: isHidden ? 'none' : 'block',
        },
    };

    // Use capabilities context
    const capabilities = useContext(CapabilityContext);
    // Only show the service-oriented pages if user has Beta UI role and we are ready to show them
    const showServiceOrientedPages = false; //capabilities.check(Capability.betaUIFeaturesRole);
    // Only show Admin if user has Bridge.Admin role
    const showAdmin = capabilities.check(Capability.srmAdminRole);
    const inPublicCloud = capabilities.check(Capability.public);
    // Kusto access is still being validated in the sovereign and air-gapped clouds, so the
    // customer page is admin-only there, and only once that cloud has a cluster configured.
    const showKustoAccess =
        inPublicCloud || (showAdmin && isKustoAccessConfiguredForCloud());

    // Assistant Chat menu entry is gated by the server-side ChatBackend:Enabled flag.
    const showChat =
        String(getConfig(GlobalConfigProperties.ChatEnabled)).toLowerCase() ===
        'true';

    // Pending-request badge. Best effort: a failure (or the feature being disabled) simply
    // leaves the badge off rather than surfacing an error in the global navigation.
    useEffect(() => {
        if (!showAdmin) {
            return undefined;
        }

        let active = true;

        const refresh = async (): Promise<void> => {
            try {
                const result = await countPendingKustoAccessRequests();
                if (active) {
                    setPendingKustoRequests(result.data?.pending ?? 0);
                }
            } catch {
                if (active) {
                    setPendingKustoRequests(0);
                }
            }
        };

        void refresh();
        window.addEventListener('focus', refresh);

        return () => {
            active = false;
            window.removeEventListener('focus', refresh);
        };
    }, [showAdmin]);

    const _links: INavLink[] = [
        {
            name: 'Home',
            url: routeKeys.Home,
            ariaLabel: 'Home',
            icon: 'HomeSolid',
            isExpanded: true,
            key: routeKeys.Home,
        },
    ];

    if (authContext.isUserAuthorized) {
        let featureSubLinks: INavLink[] = [
            {
                name: 'Releases',
                url: routeKeys.Features.Releases.Latest,
                ariaLabel: 'Releases',
                icon: 'Rocket',
                isExpanded: true,
                key: routeKeys.Features.Releases.Base,
            },
        ];

        featureSubLinks.push({
            name: 'Builds',
            url: routeKeys.Features.Builds.Latest,
            ariaLabel: 'Builds',
            icon: 'AllApps',
            isExpanded: true,
            key: routeKeys.Features.Builds.Base,
        });

        _links.push({
            name: 'Features',
            url: routeKeys.Features.Releases.Latest,
            ariaLabel: 'Features',
            icon: 'Product',
            isExpanded: true,
            key: routeKeys.Features.Base,
            links: featureSubLinks,
        });

        if (showKustoAccess) {
            _links.push({
                name: 'Onboarding',
                url: routeKeys.Onboarding.Kusto.Base,
                ariaLabel: 'Onboarding',
                icon: 'FollowUser',
                isExpanded: true,
                key: routeKeys.Onboarding.Base,
                links: [
                    {
                        name: 'Kusto',
                        url: routeKeys.Onboarding.Kusto.Base,
                        ariaLabel: 'Kusto access',
                        icon: 'Database',
                        isExpanded: true,
                        key: routeKeys.Onboarding.Kusto.Base,
                    },
                ],
            });
        }

        if (showServiceOrientedPages) {
            _links.push(
                {
                    name: 'Separator',
                    url: '',
                },
                {
                    name: 'Services',
                    url: routeKeys.Services,
                    ariaLabel: 'Services',
                    icon: 'AllApps',
                    isExpanded: true,
                    key: routeKeys.Services,
                },
                {
                    name: 'Organizations',
                    url: routeKeys.Organizations,
                    ariaLabel: 'Organizations',
                    icon: 'WorkforceManagement',
                    isExpanded: true,
                    key: routeKeys.Organizations,
                }
            );
        }
    }

    if (showAdmin) {
        const adminLinks: INavLink[] = [
            {
                name: 'Cloud Status',
                url: routeKeys.CloudStatus,
                ariaLabel: 'Cloud Status',
                icon: 'Cloud',
                key: routeKeys.CloudStatus,
            },
            {
                name: 'Environment Data',
                url: routeKeys.EnvironmentData,
                ariaLabel: 'Environment Data',
                icon: 'ServerEnviroment',
                key: routeKeys.EnvironmentData,
            },
            {
                name: 'DRI Tools',
                url: routeKeys.DRITools,
                ariaLabel: 'DRI Tools',
                icon: 'Toolbox',
                key: routeKeys.DRITools,
            },
            {
                name: pendingKustoRequests
                    ? `Kusto Admin (${pendingKustoRequests})`
                    : 'Kusto Admin',
                url: routeKeys.KustoAdmin,
                ariaLabel: pendingKustoRequests
                    ? `Kusto Admin, ${pendingKustoRequests} requests awaiting review`
                    : 'Kusto Admin',
                icon: 'Permissions',
                key: routeKeys.KustoAdmin,
            },
        ];

        if (showChat) {
            adminLinks.push({
                name: 'SRM Assistant',
                url: routeKeys.AssistantChat,
                ariaLabel: 'SRM Assistant',
                icon: 'Robot',
                key: routeKeys.AssistantChat,
            });
        }

        _links.push(
            {
                name: 'Separator',
                url: '',
            },
            {
                name: 'Administration',
                url: routeKeys.Administration,
                ariaLabel: 'Administration',
                icon: 'Settings',
                isExpanded: true,
                key: routeKeys.Administration,
                links: adminLinks,
            }
        );
    }

    const navLinkGroups: INavLinkGroup[] = [
        {
            links: _links,
        },
    ];

    const chevronClick = () => {
        setIsHidden(!isHidden);
    };

    return (
        <div
            className="PageNav-root"
            style={{
                boxShadow: DefaultEffects.elevation8,
                backgroundColor: theme.surfaces.chrome,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                zIndex: 2,
            }}
        >
            <div>
                <Nav
                    styles={navStyles}
                    ariaLabel="Navigation menu"
                    groups={navLinkGroups}
                    selectedKey={selectedRouteKey}
                    linkAs={(props) => {
                        if (props.link!.name === 'Separator') {
                            return (
                                <hr
                                    role="none"
                                    aria-hidden="true"
                                    style={{
                                        color: 'gray',
                                        margin: '0 15px',
                                    }}
                                />
                            );
                        }
                        return (
                            <Link
                                className={props.className}
                                style={{ boxSizing: 'border-box' }}
                                to={props.href!}
                            >
                                <span
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        paddingTop: isHidden ? '5px' : '0px',
                                    }}
                                >
                                    {!!props.iconProps && (
                                        <IconButton
                                            iconProps={props.iconProps}
                                            tabIndex={-1}
                                            aria-hidden="true"
                                            styles={{
                                                root: { pointerEvents: 'none' },
                                            }}
                                        />
                                    )}
                                    {props.children}
                                </span>
                            </Link>
                        );
                    }}
                />
            </div>

            <IconButton
                styles={collapseChevronStyles}
                iconProps={{
                    iconName: isHidden
                        ? 'DoubleChevronLeftMedMirrored'
                        : 'DoubleChevronLeftMed',
                }}
                onClick={chevronClick}
                ariaLabel={
                    isHidden
                        ? 'Expand navigation menu'
                        : 'Collapse navigation menu'
                }
                title={isHidden ? 'Expand' : 'Collapse'}
                aria-expanded={!isHidden}
            />
        </div>
    );
};
